import React, { useMemo } from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { SortDown, SortUp } from "../ui/icon";

interface DefaultTableProps<T = unknown> {
    data: T[];
    columns: {
        key: keyof T;
        header: string | React.ReactNode;
        footer?: string;
        renderCell?: (props: { record: T; key: keyof T }) => JSX.Element;
    }[];
    useSort?: boolean;
}

export const DefaultTable = <T extends object>(
    props: DefaultTableProps<T>,
): JSX.Element => {
    const useSort = props.useSort ?? true;
    const [sorting, setSorting] = React.useState<SortingState>(
        useSort && props.columns.some(c => c.key === "count")
            ? [{ id: "count" as string, desc: true }]
            : [],
    );

    const columns: ColumnDef<T>[] = useMemo(
        () =>
            props.columns.map(c => {
                return {
                    accessorKey: c.key as string,
                    header:
                        typeof c.header === "string"
                            ? c.header
                            : () => c.header as React.ReactElement,
                    footer: typeof c.footer === "string" ? c.footer : undefined,
                    cell: c.renderCell
                        ? info => {
                              return c.renderCell({
                                  record: info.row.original,
                                  key: c.key,
                              });
                          }
                        : info => info.getValue(),
                };
            }),
        [props.columns],
    );
    const data: T[] = useMemo(() => props.data, [props.data]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting: useSort ? sorting : undefined,
        },
        onSortingChange: useSort ? setSorting : undefined,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: useSort ? getSortedRowModel() : undefined,
        enableSorting: useSort,
    });
    return (
        <table className="w-full">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                className="py-1 px-2 bg-gray-200 text-gray-600 uppercase text-sm leading-normal"
                                onClick={
                                    useSort
                                        ? header.column.getToggleSortingHandler()
                                        : undefined
                                }>
                                <div className="flex items-center">
                                    <span>
                                        {useSort && header.column.getIsSorted() ? (
                                            header.column.getIsSorted() === "desc" ? (
                                                <SortDown className="mr-1" />
                                            ) : (
                                                <SortUp className="mr-1" />
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                    <span>
                                        {typeof header.column.columnDef.header ===
                                            "string" ||
                                        React.isValidElement(
                                            header.column.columnDef.header,
                                        )
                                            ? header.column.columnDef.header
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext(),
                                              )}
                                    </span>
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map((row, i) => {
                    return (
                        <tr
                            key={row.id}
                            className={`border-b border-gray-200 hover:bg-gray-100 ${
                                i % 2 === 1 ? "bg-gray-50" : "bg-white"
                            }`}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id} className="py-1 px-2">
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
                {columns.some(c => typeof c.footer === "string") ? (
                    <tr
                        className={`border-b border-gray-200 ${
                            table.getRowModel().rows.length % 2 === 0
                                ? `bg-white`
                                : `bg-gray-50`
                        }`}>
                        {table.getFooterGroups()[0]?.headers.map(header => (
                            <td
                                key={header.id}
                                className="py-1 px-2 text-gray-600 font-bold">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ) : null}
            </tbody>
        </table>
    );
};
